import type { ComponentPropsWithoutRef } from "react";
import { differenceInMinutes, format } from "date-fns";
import { t } from "ttag";

import { APPOINTMENT_TYPE } from "@/features/consultations/models";
import type { Appointment } from "@/features/consultations/models";
import { isMedicationRequestAppointment } from "@/features/consultations/utils";
import { useBoolean } from "@/shared/hooks";
import { formatTime } from "@/shared/lib";
import {
  Button,
  ClockIcon,
  DetailCard,
  DropdownMenu,
  IconWrapper,
  MenuDotsIcons,
  RestartCircleIcon,
  TrashBin2Icon,
  UserCircleIcon,
  VideoCameraIcon,
} from "@/shared/ui";
import { tw } from "@/shared/utils";
import { useCanJoinConsultation } from "../hooks/use-can-join-consultation";
import { CancelAppointmentModal } from "./cancel-appointment-modal";
import { JoinNowButton } from "./join-now-button";
import { RescheduleAppointmentModal } from "./reschedule-appointment-modal";

const getProfessionalLabel = (appointmentType: Appointment["type"]) => {
  switch (appointmentType) {
    case APPOINTMENT_TYPE.MEDICATION_REQUEST_INITIAL:
    case APPOINTMENT_TYPE.MEDICATION_REQUEST_FOLLOW_UP:
    case APPOINTMENT_TYPE.OTHER:
      return t`Provider`;
    case APPOINTMENT_TYPE.DIETITIAN:
      return t`Dietitian`;
    case APPOINTMENT_TYPE.JOURNEY_SUPPORT:
      return t`Coach`;
    default:
      return t`Professional`;
  }
};

type AppointmentCardProps = ComponentPropsWithoutRef<typeof DetailCard.Root> & {
  appointment: Appointment;
};

export const AppointmentCard = ({
  appointment,
  className,
  ...props
}: AppointmentCardProps) => {
  const {
    value: showCancelAppointmentModal,
    setTrue: openCancelAppointmentModal,
    setFalse: closeCancelAppointmentModal,
  } = useBoolean(false);

  const {
    value: showRescheduleAppointmentModal,
    setTrue: openRescheduleAppointmentModal,
    setFalse: closeRescheduleAppointmentModal,
  } = useBoolean(false);

  const { canJoin } = useCanJoinConsultation(
    appointment.startTime,
    appointment.endTime,
  );

  const isMedicationRequest = isMedicationRequestAppointment(appointment.type);

  const isRescheduleDisabled = !appointment.rescheduleUrl;

  return (
    <>
      <DetailCard.Root
        color={isMedicationRequest ? "brown" : "nature"}
        variant="borderLess"
        className={tw(
          "max-w-full gap-4 border border-secondary-200",
          className,
        )}
        {...props}
      >
        <DetailCard.Header className="items-center">
          <IconWrapper size="sm">
            <VideoCameraIcon />
          </IconWrapper>
          <DetailCard.Title className="line-clamp-1">
            {appointment.title}
          </DetailCard.Title>
        </DetailCard.Header>

        <DetailCard.Content className="px-0">
          <div className="mt-auto flex flex-wrap gap-2 sm:flex-nowrap">
            <DetailCard.Badge color="light-primary">
              {appointment.startTime
                ? format(appointment.startTime, "MM/dd/yy")
                : "-"}
            </DetailCard.Badge>
            <DetailCard.Badge outline>
              {formatTime(appointment.startTime)}
            </DetailCard.Badge>
          </div>

          <div className="flex gap-4">
            {appointment.provider?.fullName && (
              <div className="flex min-w-0 flex-1 items-center gap-1">
                <UserCircleIcon className="size-3.5" />
                <DetailCard.Label>
                  {getProfessionalLabel(appointment.type)}
                </DetailCard.Label>
                <DetailCard.Description className="truncate text-xs">
                  {appointment.provider.fullName}
                </DetailCard.Description>
              </div>
            )}

            <div className="flex items-center gap-1 only:ml-auto">
              <DetailCard.Description className="text-xs">
                {differenceInMinutes(
                  appointment.endTime,
                  appointment.startTime,
                )}
              </DetailCard.Description>
              <DetailCard.Label>{t`min`}</DetailCard.Label>
              <ClockIcon className="size-3.5" />
            </div>
          </div>
        </DetailCard.Content>

        <DetailCard.Footer className="gap-2 px-0">
          <JoinNowButton canJoin={canJoin} videoUrl={appointment.videoUrl} />

          <DropdownMenu.Root modal={false}>
            <DropdownMenu.Trigger asChild>
              <Button className="h-auto px-4" variant="secondary" size="lg">
                <MenuDotsIcons className="size-4 rotate-90" />
              </Button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Content>
              <DropdownMenu.Item className="focus:bg-transparent" asChild>
                <Button
                  variant="tertiary"
                  className="justify-start pl-0"
                  onClick={openRescheduleAppointmentModal}
                  disabled={isRescheduleDisabled}
                >
                  <RestartCircleIcon className="size-4" />
                  {t`Reschedule`}
                </Button>
              </DropdownMenu.Item>

              <DropdownMenu.Separator />

              <DropdownMenu.Item className="focus:bg-transparent" asChild>
                <Button
                  variant="tertiary"
                  color="red"
                  className="justify-start pl-0 enabled:hover:text-red-700!"
                  onClick={openCancelAppointmentModal}
                  disabled={
                    appointment.type ===
                    APPOINTMENT_TYPE.MEDICATION_REQUEST_INITIAL
                  }
                >
                  <TrashBin2Icon className="size-4" />
                  {t`Cancel`}
                </Button>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </DetailCard.Footer>
      </DetailCard.Root>

      <CancelAppointmentModal
        show={showCancelAppointmentModal}
        onClose={closeCancelAppointmentModal}
        appointment={appointment}
      />

      <RescheduleAppointmentModal
        show={showRescheduleAppointmentModal}
        onClose={closeRescheduleAppointmentModal}
        appointment={appointment}
      />
    </>
  );
};
